import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './InputSection.module.scss';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/AttachWhite.svg';
import {
  getBusinessArea,
  useAppSelector,
  useAppDispatch,
  setBusinessArea,
  getBootstrapApp,
  getService,
  setDisclaimerModal,
  setCurrentFile,
  setInputSize,
  getCurrentFile
} from 'app/redux';
import { handleFileSelection, handleFileValidation } from 'app/utils';
import config from 'config';
import { SERVICE_NAMES } from 'app/utils/consts';
import PDFFileIcon from 'app/assets/Icons/PDFFile.svg';
import MP3Filecon from 'app/assets/Icons/MP3File.svg';
import { ReactComponent as EraserIcon } from 'app/assets/Icons/Eraser.svg';

export const InputSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const bootstrap = useAppSelector(getBootstrapApp);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const currentService = useAppSelector(getService);
  const currentFile = useAppSelector(getCurrentFile);
  const businessAreas = bootstrap.businessAreas;
  const [errorText, setErrorText] = useState<string | undefined>();
  const [fileSizeInput, setFileSizeInput] = useState<string | undefined>();

  const getUndefinedBA = (): void => {
    if (currentService !== undefined && currentBusinessArea === undefined) {
      for (const property in businessAreas) {
        if (businessAreas[property].services.includes(String(currentService)))
          dispatch(setBusinessArea(property));
      }
    }
  };

  const bytesToMB = (bytes: number): number => {
    return bytes / (1024 * 1024);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    const selectedFile: File | undefined = files ? files[0] : undefined;

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        if (currentBusinessArea === undefined) getUndefinedBA();
        dispatch(setCurrentFile(selectedFile));
        dispatch(setInputSize(bytesToMB(selectedFile.size)));
        return;
      }
      const errorMessage = validation.error;
      console.error(errorMessage);
      setErrorText(errorMessage);
    }
  };

  const handleSelectionClick = (e: { stopPropagation: () => void }): void => {
    if (!currentBusinessArea) getUndefinedBA();
    if (currentService === undefined) {
      dispatch(setDisclaimerModal(true));
    } else {
      handleFileSelection(handleFileChange, currentService);
    }
  };

  const handleFileSizeInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFileSizeInput(e.target.value);
  };

  useEffect(() => {
    let newInputSize: number | undefined;

    if (fileSizeInput !== undefined) {
      if (fileSizeInput.trim() === '') {
        setErrorText(undefined);
      } else {
        const size = Number(fileSizeInput);

        if (size > config.APP.MAX_FILESIZE) {
          const errorMessage = `File size limit is ${config.APP.MAX_FILESIZE}MB`;
          console.error(errorMessage);
          setErrorText(errorMessage);
        } else if (size <= 0) {
          const errorMessage = `File size should be greater than 0MB`;
          console.error(errorMessage);
          setErrorText(errorMessage);
        } else {
          setErrorText(undefined);
          if (currentBusinessArea === undefined) getUndefinedBA();
          newInputSize = size;
        }
      }
    }

    dispatch(setInputSize(newInputSize));
  }, [fileSizeInput]);

  useEffect(() => {
    setFileSizeInput(undefined);
    setErrorText(undefined);
  }, [currentService, currentBusinessArea]);

  const renderUploadFileButton = (): React.JSX.Element => (
    <>
      <h3 className={styles['heading-3']}>3. Upload Document</h3>
      <Button onClick={handleSelectionClick}>
        <Button.Icon>
          <AttachIcon />
        </Button.Icon>
        <Button.Label>Upload File</Button.Label>
      </Button>
      <span className={styles.error}>{errorText}</span>
    </>
  );

  const renderFileSizeInput = (): React.JSX.Element => (
    <>
      <h3 className={styles['heading-3']}>3. Specify Input Size</h3>
      <input
        type="number"
        placeholder="Input Size in MB"
        className={styles['text-area']}
        spellCheck="false"
        onChange={handleFileSizeInputChange}
        value={fileSizeInput || ''}
      />
      <span className={styles.error}>{errorText}</span>
    </>
  );

  const handleRemoveFile = (): void => {
    dispatch(setCurrentFile(undefined));
    dispatch(setInputSize(undefined));
  };

  const renderUploadedDocument = (): React.JSX.Element => (
    <>
      <h3 className={styles['heading-3']}>3. Upload Document</h3>
      {currentFile && (
        <div className={styles.file}>
          <div className={styles.details}>
            <img
              src={
                currentFile.type.includes('audio/') ? MP3Filecon : PDFFileIcon
              }
              alt=""
              aria-hidden
              className={styles.icon}
            />

            <p
              className={classnames({
                [styles.filename]: currentFile?.name.length > 35
              })}
            >
              {currentFile?.name.length > 35
                ? currentFile?.name.slice(0, 35) + '...'
                : currentFile?.name}
              {currentFile?.name.length > 35 && (
                <span className={styles.tooltip}>{currentFile?.name}</span>
              )}
            </p>
            <Button
              onClick={handleRemoveFile}
              variant="icon"
              aria-label="remove"
              className={styles.remove}
            >
              <Button.Icon>
                <EraserIcon />
              </Button.Icon>
            </Button>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className={styles['upload-document']} id="upload-document">
      {(() => {
        if (
          currentService === SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY ||
          currentService === SERVICE_NAMES.TRANSLATOR
        ) {
          return renderFileSizeInput();
        }
        if (currentFile) {
          return renderUploadedDocument();
        }
        return renderUploadFileButton();
      })()}
    </div>
  );
};
